import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FileText, Send, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { trackInteraction } from "@/lib/performance";
import { trackConversion } from "@/config/analytics";
import { parseFunctionError, type EmailDebugError } from "@/lib/emailDebug";
import { useLanguage } from "@/contexts/LanguageContext";
import { SectionHeading } from "@/components/ui/section-heading";

interface QuoteFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  serviceType: string;
  projectDetails: string;
  timeline: string;
  budget: string;
}

const emptyQuoteForm: QuoteFormData = {
  name: "",
  email: "",
  phone: "",
  company: "",
  serviceType: "",
  projectDetails: "",
  timeline: "",
  budget: "",
};

const RequestQuote = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === "ar";
  const t = {
    eyebrow: isAr ? "ابدأ مشروعك" : "Start your project",
    h1: isAr ? "اطلب عرض" : "Request a",
    h2: isAr ? "سعر" : "Quote",
    sub: isAr
      ? "احصل على عرض مفصّل لمشروعك. املأ النموذج أدناه وسيتواصل فريقنا خلال 24 ساعة."
      : "Get a detailed quote for your project. Fill out the form below and our team will respond within 24 hours.",
  };
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch, clearErrors } = useForm<QuoteFormData>({
    defaultValues: emptyQuoteForm,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<EmailDebugError | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<"idle" | "submitting" | "sent" | "failed">("idle");
  const submissionLock = useRef(false);
  const { toast } = useToast();
  const selectedServiceType = watch("serviceType");

  const onSubmit = async (data: QuoteFormData) => {
    if (submissionLock.current) return;
    submissionLock.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    setSubmissionStatus("submitting");
    clearErrors();
    reset(emptyQuoteForm, {
      keepErrors: false,
      keepDirty: false,
      keepTouched: false,
      keepIsSubmitted: false,
    });

    trackInteraction('quote-form-submission', async () => {
      try {
        const payload: Record<string, unknown> = { ...data };

        const { data: resData, error } = await supabase.functions.invoke('send-quote-request', {
          body: payload,
        });

        if (error) throw error;
        if (resData && (resData as any).error) {
          throw new Error((resData as any).error);
        }

        setSubmitSuccess(true);
        setSubmissionStatus("sent");
        trackConversion("quote_form_submit");
        toast({
          title: "Quote Request Sent!",
          description: "Your request has been received. We'll get back to you within 24 hours.",
        });
        navigate("/quote-confirmation", {
          state: {
            status: "sent",
            name: data.name,
            serviceType: data.serviceType,
            referenceId: (resData as any)?.referenceId,
            email: data.email,
          },
        });
      } catch (err: any) {
        console.error('Quote request error:', err);

        const parsedError = await parseFunctionError(
          err,
          "Could not send your request",
          "We couldn't reach our email service. Please try again in a few minutes, or email sales@arabianalwaha.com directly.",
        );
        const status = parsedError.debug?.edgeStatusCode ?? err?.context?.status ?? err?.status;
        const title = status === 429 ? "Too many requests" : parsedError.title;
        const details = status === 429
          ? ["You've sent several quote requests recently. Please wait about an hour and try again, or email sales@arabianalwaha.com directly."]
          : ["We couldn't send your quote request right now. Please try again in a few minutes, or email sales@arabianalwaha.com directly."];

        setSubmitError({ ...parsedError, title, details });
        setSubmissionStatus("failed");
        toast({
          title,
          description: details[0],
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
        submissionLock.current = false;
      }
    });
  };

  const onInvalidSubmit = () => {
    setSubmissionStatus("idle");
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  return (
    <section id="quote" className="py-20 bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="heading-eyebrow">{t.eyebrow}</span>
          <div className="inline-flex items-center gap-2 mt-2">
            <FileText className="w-8 h-8 text-heading-accent" />
            <h2 className="heading-brand text-4xl lg:text-5xl">
              {t.h1} <span className="brand-accent">{t.h2}</span>
            </h2>
          </div>
          <div className="heading-bar mx-auto mb-4" />
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t.sub}
          </p>
        </div>

        <Card className="max-w-3xl mx-auto p-8 shadow-lg">
          <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} className="space-y-6">
            {submitSuccess && (
              <Alert className="border-primary/40 bg-primary/5">
                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                <AlertTitle>Request sent</AlertTitle>
                <AlertDescription>
                  Thank you! Our team will get back to you within 24 hours.
                </AlertDescription>
              </Alert>
            )}

            {submissionStatus === "submitting" && (
              <Alert className="border-secondary/50 bg-secondary/10">
                <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
                <AlertTitle>Submitting request</AlertTitle>
                <AlertDescription>Please wait while we send your quote request.</AlertDescription>
              </Alert>
            )}

            {submitError && (
              <Alert variant="destructive" role="alert">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>{submitError.title}</AlertTitle>
                <AlertDescription>
                  {submitError.details.length === 1 ? (
                    <p>{submitError.details[0]}</p>
                  ) : (
                    <ul className="list-disc pl-5 space-y-1">
                      {submitError.details.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  {...register("name", { required: "Name is required" })}
                  placeholder="John Doe"
                  className={errors.name ? "border-destructive" : ""}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  placeholder="john@company.com"
                  className={errors.email ? "border-destructive" : ""}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  {...register("phone", { required: "Phone is required" })}
                  placeholder="+966 XX XXX XXXX"
                  className={errors.phone ? "border-destructive" : ""}
                />
                {errors.phone && (
                  <p className="text-sm text-destructive">{errors.phone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company Name *</Label>
                <Input
                  id="company"
                  {...register("company", { required: "Company name is required" })}
                  placeholder="Company Ltd."
                  className={errors.company ? "border-destructive" : ""}
                />
                {errors.company && (
                  <p className="text-sm text-destructive">{errors.company.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serviceType">Service Type *</Label>
              <Select value={selectedServiceType} onValueChange={(value) => setValue("serviceType", value)}>
                <SelectTrigger 
                  className={errors.serviceType ? "border-destructive" : ""}
                  aria-label="Select a service type for your quote request"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manpower">Manpower Supply</SelectItem>
                  <SelectItem value="material">Material Supply</SelectItem>
                  <SelectItem value="equipment">Equipment Rental</SelectItem>
                  <SelectItem value="vehicle">Vehicle Rental</SelectItem>
                  <SelectItem value="maintenance">Maintenance Services</SelectItem>
                  <SelectItem value="consulting">Consulting Services</SelectItem>
                  <SelectItem value="other">Other Services</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" {...register("serviceType", { required: "Service type is required" })} />
              {errors.serviceType && (
                <p className="text-sm text-destructive">{errors.serviceType.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="projectDetails">Project Details *</Label>
              <Textarea
                id="projectDetails"
                {...register("projectDetails", { required: "Project details are required" })}
                placeholder="Describe your project requirements, scope, and any specific needs..."
                className={`min-h-[120px] ${errors.projectDetails ? "border-destructive" : ""}`}
              />
              {errors.projectDetails && (
                <p className="text-sm text-destructive">{errors.projectDetails.message}</p>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="timeline">Project Timeline</Label>
                <Input
                  id="timeline"
                  {...register("timeline")}
                  placeholder="e.g., 3 months, ASAP"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="budget">Budget Range (Optional)</Label>
                <Input
                  id="budget"
                  {...register("budget")}
                  placeholder="e.g., SAR 50,000 - 100,000"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 text-lg font-semibold"
              aria-label={isSubmitting ? "Sending quote request..." : "Submit quote request form"}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
                  Sending your request...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" aria-hidden="true" />
                  Request Quote
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
};

export default RequestQuote;
